import { createContext, useContext, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const UndoContext = createContext(null);

const MAX_STACK_SIZE = 20;
const TOAST_DURATION_MS = 8000;

// The server's reason (plan limit, not found, ...) so a failed undo says why.
const failureReason = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return err?.response ? undefined : err?.message;
};

export const useUndo = () => {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndo must be used within UndoProvider');
  return ctx;
};

export const UndoProvider = ({ children }) => {
  const { t } = useTranslation();
  // useRef namiesto useState → stack nespôsobuje re-rendery
  const stackRef = useRef([]);

  const pushUndo = useCallback(({ label, undo, onUndone }) => {
    const action = { label, undo, onUndone };
    stackRef.current = [action, ...stackRef.current].slice(0, MAX_STACK_SIZE);

    toast(label, {
      duration: TOAST_DURATION_MS,
      action: {
        label: t('undo.undo'),
        onClick: async () => {
          stackRef.current = stackRef.current.filter(a => a !== action);
          try {
            await undo();
            onUndone?.();
            toast.success(t('undo.action_undone'));
          } catch (err) {
            console.error('[Undo] Failed:', err);
            toast.error(t('ui_common.undo.failed'), { description: failureReason(err) });
          }
        },
      },
    });
  }, [t]);

  // Ctrl+Z / Cmd+Z - vyskočí poslednú akciu zo stacku
  const triggerUndo = useCallback(async () => {
    if (!stackRef.current.length) return;
    const [action, ...rest] = stackRef.current;
    stackRef.current = rest;
    try {
      await action.undo();
      action.onUndone?.();
      toast.success(t('ui_common.undo.undone_label', { label: action.label }));
    } catch (err) {
      console.error('[Undo] Keyboard undo failed:', err);
      toast.error(t('ui_common.undo.failed'), { description: failureReason(err) });
    }
  }, [t]);

  // Vymazanie stacku pri logout
  const clearUndoStack = useCallback(() => {
    stackRef.current = [];
  }, []);

  return (
    <UndoContext.Provider value={{ pushUndo, triggerUndo, clearUndoStack }}>
      {children}
    </UndoContext.Provider>
  );
};

export default UndoContext;
